export interface Industry {
  id: string;
  name: string;
}

export const INDUSTRIES: Industry[] = [
  { id: "technology", name: "Technology" },
  { id: "healthcare", name: "Healthcare" },
  { id: "finance", name: "Finance & Banking" },
  { id: "ecommerce", name: "E-Commerce & Retail" },
  { id: "manufacturing", name: "Manufacturing" },
  { id: "education", name: "Education" },
  { id: "real-estate", name: "Real Estate" },
  { id: "legal", name: "Legal Services" },
  { id: "media", name: "Media & Entertainment" },
  { id: "logistics", name: "Logistics & Transportation" },
  { id: "hospitality", name: "Hospitality & Travel" },
  { id: "energy", name: "Energy & Utilities" },
  { id: "nonprofit", name: "Non-Profit" },
  { id: "government", name: "Government" },
  { id: "other", name: "Other" },
];

export type CompanySize = "1-10" | "10-50" | "50-150" | "150+";

export const COMPANY_SIZES: CompanySize[] = ["1-10", "10-50", "50-150", "150+"];

export type BudgetRange =
  | "$1,000-$5,000"
  | "$5,000-$10,000"
  | "$10,000-$25,000"
  | "$25,000-$50,000"
  | "$50,000-$100,000"
  | "$100,000+";

export const BUDGET_RANGES: BudgetRange[] = [
  "$1,000-$5,000",
  "$5,000-$10,000",
  "$10,000-$25,000",
  "$25,000-$50,000",
  "$50,000-$100,000",
  "$100,000+",
];

export type MeetingDuration = 30 | 45 | 60;

export const MEETING_DURATIONS: {
  duration: MeetingDuration;
  label: string;
  description: string;
  calendlySlug: string;
}[] = [
  { duration: 30, label: "30 min", description: "Quick check-in", calendlySlug: "consultation-30-min" },
  { duration: 45, label: "45 min", description: "Standard consultation", calendlySlug: "consultation-45-min" },
  { duration: 60, label: "60 min", description: "Deep dive session", calendlySlug: "consultation-60-min" },
];

export type OptimizerTierIntent = "starter" | "growth" | "scale";

export const OPTIMIZER_TIER_OPTIONS: {
  id: OptimizerTierIntent;
  name: string;
  price: string;
  responseTime: string;
}[] = [
  {
    id: "starter",
    name: "Starter",
    price: "$3,000/mo",
    responseTime: "24 business hours",
  },
  {
    id: "growth",
    name: "Growth",
    price: "$5,000/mo",
    responseTime: "8 business hours",
  },
  {
    id: "scale",
    name: "Scale",
    price: "$7,000/mo",
    responseTime: "4 hours",
  },
];

export const TIMEZONES = [
  { id: "America/New_York", name: "Eastern (ET)" },
  { id: "America/Chicago", name: "Central (CT)" },
  { id: "America/Denver", name: "Mountain (MT)" },
  { id: "America/Los_Angeles", name: "Pacific (PT)" },
  { id: "America/Anchorage", name: "Alaska (AKT)" },
  { id: "Pacific/Honolulu", name: "Hawaii (HT)" },
];

export interface ProjectBasicsData {
  name: string;
  companyName: string;
  email: string;
  projectSummary: string;
  industryId: string;
  companySize: CompanySize | "";
  selectedPackageId: string | null;
  timezone: string;
  preferredMeetingLength: MeetingDuration;
  currentSituation?: string;
  improvementGoals?: string;
  cloudProviders?: string;
  environmentCount?: string;
  hasMonitoring?: string;
  tierIntent?: OptimizerTierIntent;
  responseTimeExpectation?: string;
}

export interface VerificationContactData {
  timezone: string;
  preferredMeetingLength: MeetingDuration;
}

export interface ConfidentialityData {
  ndaRequested: boolean;
  willShareSensitiveData: boolean;
  safeSummary: string;
}

export type IntakeMode = "call" | "package";

export interface QuickBasicsData {
  name: string;
  companyName: string;
  email: string;
  discussionTopic: string;
  industryId: string;
  timezone: string;
}

export interface IntakeFormData {
  projectBasics: ProjectBasicsData;
  verificationContact: VerificationContactData;
  confidentiality: ConfidentialityData;
}

export interface IntakeLightFormData {
  quickBasics: QuickBasicsData;
  verificationContact: VerificationContactData;
}

export const initialProjectBasicsData: ProjectBasicsData = {
  name: "",
  companyName: "",
  email: "",
  projectSummary: "",
  industryId: "",
  companySize: "",
  selectedPackageId: null,
  timezone:
    Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  preferredMeetingLength: 45,
};

export const initialVerificationContactData: VerificationContactData = {
  timezone:
    Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  preferredMeetingLength: 45,
};

export const initialConfidentialityData: ConfidentialityData = {
  ndaRequested: false,
  willShareSensitiveData: false,
  safeSummary: "",
};

export const initialQuickBasicsData: QuickBasicsData = {
  name: "",
  companyName: "",
  email: "",
  discussionTopic: "",
  industryId: "",
  timezone:
    Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
};

export const initialLightVerificationContactData: VerificationContactData = {
  timezone:
    Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  preferredMeetingLength: 30,
};

export interface ConsultationFormData {
  meetingDuration: MeetingDuration;
  discussionTopics: string;
  hasDesignMockups: boolean;
  hasTechnicalDocs: boolean;
  needsRequirementsHelp: boolean;
}

export const initialConsultationData: ConsultationFormData = {
  meetingDuration: 45,
  discussionTopics: "",
  hasDesignMockups: false,
  hasTechnicalDocs: false,
  needsRequirementsHelp: false,
};

export interface OnboardingFormData {
  name: string;
  companyName: string;
  email: string;
  projectSummary: string;
  industryId: string;
  companySize: CompanySize | "";
  cloudProvider: string;
  cloudFamiliarity: string;
  selectedPackageId: string | null;
  selectedVariantId: string | null;
  budget: BudgetRange | "";
}

export const initialOnboardingData: OnboardingFormData = {
  name: "",
  companyName: "",
  email: "",
  projectSummary: "",
  industryId: "",
  companySize: "",
  cloudProvider: "",
  cloudFamiliarity: "",
  selectedPackageId: null,
  selectedVariantId: null,
  budget: "",
};

export const CLOUD_PROVIDERS: { id: string; name: string }[] = [
  { id: "aws", name: "Amazon Web Services (AWS)" },
  { id: "azure", name: "Microsoft Azure" },
  { id: "gcp", name: "Google Cloud Platform (GCP)" },
  { id: "digitalocean", name: "DigitalOcean" },
  { id: "multi", name: "Multi-Cloud" },
  { id: "none", name: "No Cloud Provider Yet" },
  { id: "other", name: "Other" },
];

export const CLOUD_FAMILIARITY_OPTIONS: { id: string; name: string }[] = [
  { id: "beginner", name: "Beginner — new to cloud" },
  { id: "intermediate", name: "Intermediate — some experience" },
  { id: "advanced", name: "Advanced — daily cloud user" },
  { id: "expert", name: "Expert — certified / architecting" },
];

export type OwnershipType = "managed" | "privileged";
export type HandoverType = "guided" | "takeover" | "direct";

export interface OwnershipFormData {
  ownershipType: OwnershipType | "";
  handoverType: HandoverType | "";
}

export const initialOwnershipData: OwnershipFormData = {
  ownershipType: "",
  handoverType: "",
};

export const OWNERSHIP_OPTIONS: {
  id: OwnershipType;
  title: string;
  description: string;
}[] = [
  {
    id: "managed",
    title: "Fully Managed",
    description:
      "We host, maintain, and operate the infrastructure on your behalf.",
  },
  {
    id: "privileged",
    title: "Client-Owned",
    description:
      "You retain full ownership and access; we build and hand over.",
  },
];

export const HANDOVER_OPTIONS: {
  id: HandoverType;
  title: string;
  description: string;
  recommended?: OwnershipType;
}[] = [
  {
    id: "guided",
    title: "Guided Walkthrough",
    description:
      "Live sessions to walk your team through the delivered solution.",
    recommended: "privileged",
  },
  {
    id: "takeover",
    title: "Full Takeover",
    description: "We continue to manage and operate everything post-delivery.",
    recommended: "managed",
  },
  {
    id: "direct",
    title: "Direct Handoff",
    description: "Code and docs delivered; your team takes over immediately.",
  },
];

export function isRecurringPackage(packageId: string | null): boolean {
  return packageId === "pkg-shipping-ops-partner";
}

export function isSensitivePackage(packageId: string | null): boolean {
  return packageId === "pkg-ai-desktop-launch";
}

export function isComplexScopingPackage(packageId: string | null): boolean {
  return (
    packageId === "pkg-ai-desktop-launch" ||
    packageId === "pkg-desktop-feasibility"
  );
}

export type EngagementPath = "call" | "questionnaire";

export type TimelineExpectation =
  | "asap"
  | "1-4-weeks"
  | "1-3-months"
  | "flexible";

export const TIMELINE_OPTIONS: { id: TimelineExpectation; label: string }[] = [
  { id: "asap", label: "ASAP" },
  { id: "1-4-weeks", label: "1-4 weeks" },
  { id: "1-3-months", label: "1-3 months" },
  { id: "flexible", label: "Flexible" },
];

export interface CommonQuestionnaireData {
  problemToSolve: string;
  successCriteria: string;
  timeline: TimelineExpectation | "";
  technicalEnvironment: string;
  constraints: string;
  decisionMakers: string;
}

export interface CloudAuditQuestionnaireData {
  currentProviders: string;
  environmentCount: string;
  monthlyCloudSpend: string;
  biggestCostDrivers: string;
  hasSecurityConcerns: boolean;
  securityConcernDetails: string;
}

export interface OptimizerQuestionnaireData {
  currentProviders: string;
  environmentCount: string;
  monthlyCloudSpend: string;
  biggestPainPoints: string[];
  hasMonitoring: boolean;
  monitoringDetails: string;
  targetTier: string;
  responseTimeExpectation: string;
}

export interface ComplianceLaunchpadQuestionnaireData {
  targetFramework: string[];
  currentProviders: string;
  hasExistingInfra: boolean;
  existingInfraDetails: string;
  iacPreference: string;
  cicdRequired: boolean;
  cicdPlatform: string;
  enterpriseDealBlocked: boolean;
}

export type PackageQuestionnaireData =
  | { type: "cloud-audit"; data: CloudAuditQuestionnaireData }
  | { type: "optimizer"; data: OptimizerQuestionnaireData }
  | { type: "compliance-launchpad"; data: ComplianceLaunchpadQuestionnaireData };

export interface QuestionnaireSubmission {
  common: CommonQuestionnaireData;
  packageSpecific?: PackageQuestionnaireData;
}

export const CLOUD_PROVIDER_OPTIONS = [
  { id: "aws", label: "AWS" },
  { id: "gcp", label: "GCP" },
  { id: "azure", label: "Azure" },
  { id: "multi", label: "Multi-Cloud" },
];

export const MONTHLY_SPEND_OPTIONS = [
  { id: "under-5k", label: "Under $5,000/mo" },
  { id: "5k-15k", label: "$5,000 – $15,000/mo" },
  { id: "15k-50k", label: "$15,000 – $50,000/mo" },
  { id: "50k-100k", label: "$50,000 – $100,000/mo" },
  { id: "100k-plus", label: "$100,000+/mo" },
  { id: "unsure", label: "Not sure" },
];

export const PAIN_POINT_OPTIONS = [
  { id: "cost", label: "Cloud costs too high" },
  { id: "security", label: "Security vulnerabilities" },
  { id: "uptime", label: "Uptime / reliability" },
  { id: "compliance", label: "Compliance requirements" },
  { id: "visibility", label: "Lack of visibility" },
  { id: "scaling", label: "Scaling challenges" },
];

export const COMPLIANCE_FRAMEWORK_OPTIONS = [
  { id: "soc2", label: "SOC 2" },
  { id: "hipaa", label: "HIPAA" },
  { id: "pci-dss", label: "PCI-DSS" },
  { id: "iso27001", label: "ISO 27001" },
  { id: "gdpr", label: "GDPR" },
  { id: "other", label: "Other" },
];

export const IAC_OPTIONS = [
  { id: "terraform", label: "Terraform" },
  { id: "pulumi", label: "Pulumi" },
  { id: "cdk", label: "CDK" },
  { id: "no-preference", label: "No preference" },
];

export const CICD_PLATFORMS = [
  { id: "github-actions", label: "GitHub Actions" },
  { id: "gitlab-ci", label: "GitLab CI" },
  { id: "aws-codepipeline", label: "AWS CodePipeline" },
  { id: "other", label: "Other" },
];

export const RESPONSE_TIME_OPTIONS = [
  { id: "24h", label: "24 hours (Starter tier)" },
  { id: "8h", label: "8 hours (Growth tier)" },
  { id: "4h", label: "4 hours (Scale tier)" },
];
